import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { AllowListItem } from 'src/features/allowList/utils/types';
import { allowListMessages } from 'src/features/allowList/utils/allowListMessages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useDeleteAllowListItem } from 'src/features/allowListItem/api/useDeleteAllowListItem';
import { useParams } from 'react-router-dom';
import { LIST_ALLOW_LIST_ITEMS_RESOURCE } from 'src/features/allowListItem/api/useListAllowListItems';
import { useParking } from 'src/providers/ParkingProvider';

interface AllowListItemFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAllowListItem: AllowListItem;
    children?: React.ReactNode;
}

export default function DeleteAllowListItemFormModal<TData>({ selectedAllowListItem, semantics, children }: AllowListItemFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { listId = '' } = useParams();
    const { parkingId } = useParking()
    const { mutate: deleteAllowList, isPending: isPendingDelete } = useDeleteAllowListItem({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOW_LIST_ITEMS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(allowListMessages.deleteAllowListItemSuccess),
                description: formatMessage(allowListMessages.deleteAllowListItemSuccessDescription, {
                    entry: selectedAllowListItem?.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deleteAllowList({ listId, itemId: selectedAllowListItem.id, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={allowListMessages.deleteAllowListItemSubtitle.id}
                                    defaultMessage={allowListMessages.deleteAllowListItemSubtitle.defaultMessage}
                                    values={{
                                        entry: selectedAllowListItem?.name,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={allowListMessages.confirmDeleteAllowListItem.id}
                            defaultMessage={allowListMessages.confirmDeleteAllowListItem.defaultMessage}
                            values={{
                                entry: selectedAllowListItem?.name,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
